/**
 * AbortController utilities for managing request cancellation
 * Provides centralized management of abort controllers for async operations
 */

export interface AbortableOperation<T = any> {
  promise: Promise<T>;
  abort: () => void;
  signal: AbortSignal;
}

/**
 * Creates an abortable operation with timeout support
 * @param operation - The async operation to make abortable
 * @param timeoutMs - Optional timeout in milliseconds
 * @returns AbortableOperation with promise, abort function, and signal
 */
export function createAbortableOperation<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  timeoutMs?: number
): AbortableOperation<T> {
  const controller = new AbortController();

  // Set up timeout if specified
  let timeoutId: NodeJS.Timeout | null = null;
  if (timeoutMs) {
    timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);
  }

  const promise = operation(controller.signal)
    .finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    })
    .catch(error => {
      if (error.name === 'AbortError') {
        throw new Error('Operation was cancelled');
      }
      throw error;
    });

  return {
    promise,
    abort: () => controller.abort(),
    signal: controller.signal
  };
}

/**
 * Manages multiple abort controllers for batch operations
 */
export class AbortControllerManager {
  private controllers: Map<string, AbortController> = new Map();
  private timeouts: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Creates a new abort controller for an operation
   * @param id - Unique identifier for the operation
   * @param timeoutMs - Optional timeout in milliseconds
   * @returns The AbortController instance
   */
  create(id: string, timeoutMs?: number): AbortController {
    // Clean up existing controller for this ID
    this.abort(id);

    const controller = new AbortController();
    this.controllers.set(id, controller);

    // Set up timeout if specified
    if (timeoutMs) {
      const timeoutId = setTimeout(() => {
        this.abort(id);
      }, timeoutMs);
      this.timeouts.set(id, timeoutId);
    }

    return controller;
  }

  /**
   * Gets an existing abort controller by ID
   * @param id - Operation identifier
   * @returns The AbortController or null if not found
   */
  get(id: string): AbortController | null {
    return this.controllers.get(id) || null;
  }

  /**
   * Aborts a specific operation
   * @param id - Operation identifier to abort
   */
  abort(id: string): void {
    const controller = this.controllers.get(id);
    if (controller) {
      controller.abort();
      this.controllers.delete(id);
    }

    const timeoutId = this.timeouts.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeouts.delete(id);
    }
  }

  /**
   * Aborts all active operations
   */
  abortAll(): void {
    this.controllers.forEach((_, id) => this.abort(id));
  }

  /**
   * Checks if an operation is active
   * @param id - Operation identifier
   * @returns True if the operation is active
   */
  isActive(id: string): boolean {
    const controller = this.controllers.get(id);
    return controller !== undefined && !controller.signal.aborted;
  }

  /**
   * Gets all active operation IDs
   * @returns Array of active operation IDs
   */
  getActiveOperations(): string[] {
    const active: string[] = [];
    this.controllers.forEach((controller, id) => {
      if (!controller.signal.aborted) {
        active.push(id);
      }
    });
    return active;
  }

  /**
   * Cleans up completed operations
   */
  cleanup(): void {
    const toRemove: string[] = [];

    this.controllers.forEach((controller, id) => {
      if (controller.signal.aborted) {
        toRemove.push(id);
      }
    });

    toRemove.forEach(id => {
      this.controllers.delete(id);
      const timeoutId = this.timeouts.get(id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.timeouts.delete(id);
      }
    });
  }

  /**
   * Destroys the manager and cleans up all resources
   */
  destroy(): void {
    this.abortAll();
    this.controllers.clear();
    this.timeouts.clear();
  }
}

// Global abort controller manager instance
export const globalAbortManager = new AbortControllerManager();

/**
 * Creates an abortable version of a function
 * @param fn - The function to make abortable
 * @param timeoutMs - Optional timeout in milliseconds
 * @returns Function that returns an AbortableOperation
 */
export function makeAbortable<T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  timeoutMs?: number
): (...args: T) => AbortableOperation<R> {
  return (...args: T) => {
    return createAbortableOperation(
      async (signal) => {
        // Check if already aborted
        if (signal.aborted) {
          throw new Error('Operation was cancelled');
        }

        // Create a promise that can be aborted
        const promise = fn(...args);

        // Set up abort handling
        const abortPromise = new Promise<never>((_, reject) => {
          signal.addEventListener('abort', () => {
            reject(new Error('Operation was cancelled'));
          });
        });

        // Race between the operation and abort signal
        return Promise.race([promise, abortPromise]);
      },
      timeoutMs
    );
  };
}

/**
 * Wraps a promise with abort signal support
 * @param promise - The promise to wrap
 * @param signal - Abort signal to monitor
 * @returns Promise that can be aborted
 */
export function withAbortSignal<T>(
  promise: Promise<T>,
  signal: AbortSignal
): Promise<T> {
  if (signal.aborted) {
    return Promise.reject(new Error('Operation was cancelled'));
  }

  return new Promise((resolve, reject) => {
    const abortHandler = () => {
      reject(new Error('Operation was cancelled'));
    };

    signal.addEventListener('abort', abortHandler);

    promise
      .then(resolve)
      .catch(reject)
      .finally(() => {
        signal.removeEventListener('abort', abortHandler);
      });
  });
}

/**
 * Creates a cancellable timeout
 * @param ms - Timeout duration in milliseconds
 * @param signal - Optional abort signal
 * @returns Promise that resolves after timeout or rejects if aborted
 */
export function cancellableTimeout(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(resolve, ms);

    if (signal) {
      const abortHandler = () => {
        clearTimeout(timeoutId);
        reject(new Error('Timeout was cancelled'));
      };

      signal.addEventListener('abort', abortHandler);

      // Clean up listener when timeout completes
      setTimeout(() => {
        signal.removeEventListener('abort', abortHandler);
      }, ms);
    }
  });
}

/**
 * Batch operation helper with abort support
 * @param operations - Array of operations to execute
 * @param signal - Abort signal for the batch operation
 * @param concurrency - Maximum concurrent operations (default: 3)
 * @returns Promise resolving to array of results
 */
export async function abortableBatch<T>(
  operations: (() => Promise<T>)[],
  signal: AbortSignal,
  concurrency: number = 3
): Promise<T[]> {
  if (signal.aborted) {
    throw new Error('Batch operation was cancelled');
  }

  const results: T[] = [];
  const errors: Error[] = [];

  for (let i = 0; i < operations.length; i += concurrency) {
    if (signal.aborted) {
      throw new Error('Batch operation was cancelled');
    }

    const batch = operations.slice(i, i + concurrency);
    const batchPromises = batch.map(operation =>
      withAbortSignal(operation(), signal).catch(error => {
        errors.push(error);
        return null as T;
      })
    );

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter(result => result !== null));
  }

  // If we have errors but no results, throw the first error
  if (errors.length > 0 && results.length === 0) {
    throw errors[0];
  }

  return results;
}
